import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckCircleFilled, CreditCardOutlined, LockOutlined } from "@ant-design/icons";
import { Button, Skeleton, theme } from "antd";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { ChangePaymentDetailsPaddleFrame } from "./changePaymentDetailsPaddleFrame.js";
import { useStores } from "../../../../store/stores.js";
import { __ } from "../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../utils/matomoMask.js";
import { useSubscriptionPaymentMethodCard } from "../../hooks/useSubscriptionPaymentMethodCard.js";
import { InfoCard } from "../infoCard.js";
const ChangePaymentDetailsForm = observer(({ subscription, shouldRefetch = true, onClose })=>{
    const { token } = theme.useToken();
    const { optionStore } = useStores();
    const [isLoaded, setIsLoaded] = useState(false);
    const [paddleError, setPaddleError] = useState();
    const [isSuccess, setIsSuccess] = useState(false);
    const currentMethod = useSubscriptionPaymentMethodCard(subscription);
    // Capture the old method BEFORE the post-success refetch overwrites
    // `paymentInformation`; otherwise the strike-through line in the success
    // surface would lose its value.
    const oldMethodRef = useRef(currentMethod);
    useEffect(()=>{
        subscription.loadVendorScripts().then(()=>setIsLoaded(true));
    }, [
        subscription
    ]);
    if (!subscription.data.ecommerceSystemSubscriptionId) {
        return null;
    }
    if (isSuccess) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(InfoCard, {
                    variant: "soft-trust",
                    leftBorderColor: token.colorSuccess,
                    children: /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: "flex",
                            flexDirection: "column",
                            gap: 8
                        },
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                style: {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    fontWeight: 600,
                                    color: token.colorTextHeading
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(CheckCircleFilled, {
                                        style: {
                                            color: token.colorSuccess,
                                            fontSize: 18
                                        }
                                    }),
                                    __("Payment method updated.")
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                style: {
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 8,
                                    fontSize: 13
                                },
                                ...MATOMO_MASK,
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        style: {
                                            textDecoration: "line-through",
                                            color: token.colorTextSecondary
                                        },
                                        children: [
                                            oldMethodRef.current.label,
                                            oldMethodRef.current.maskedLast4 ? ` (${oldMethodRef.current.maskedLast4})` : ""
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        style: {
                                            color: token.colorTextSecondary
                                        },
                                        children: "→"
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        style: {
                                            fontWeight: 500,
                                            color: token.colorTextHeading
                                        },
                                        children: [
                                            currentMethod.label,
                                            currentMethod.maskedLast4 ? ` (${currentMethod.maskedLast4})` : ""
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        marginTop: 16,
                        display: "flex",
                        justifyContent: "flex-end"
                    },
                    children: /*#__PURE__*/ _jsx(Button, {
                        type: "primary",
                        onClick: onClose,
                        children: __("Close")
                    })
                })
            ]
        });
    }
    const paymentMethodsUrl = `${optionStore.publicUrl}images/payment-methods.png`;
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            paddingTop: 8
        },
        children: [
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 10,
                    marginBottom: 16
                },
                children: [
                    /*#__PURE__*/ _jsx("strong", {
                        style: {
                            color: token.colorTextHeading
                        },
                        children: __("Current payment method:")
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        ...MATOMO_MASK,
                        style: {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "4px 10px",
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: 6,
                            fontWeight: 500,
                            color: token.colorTextHeading
                        },
                        children: [
                            /*#__PURE__*/ _jsx(CreditCardOutlined, {
                                style: {
                                    color: token.colorTextSecondary
                                }
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: currentMethod.label
                            }),
                            currentMethod.maskedLast4 && /*#__PURE__*/ _jsxs("span", {
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: [
                                    "•• ",
                                    currentMethod.maskedLast4
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-change-payment__buy-area",
                style: {
                    background: "rgba(69, 179, 221, 0.06)",
                    border: "1px solid rgba(69, 179, 221, 0.18)",
                    borderRadius: 8,
                    padding: 16
                },
                children: [
                    isLoaded ? /*#__PURE__*/ _jsx(ChangePaymentDetailsPaddleFrame, {
                        subscription: subscription,
                        shouldRefetch: shouldRefetch,
                        onComplete: ()=>setIsSuccess(true),
                        onError: (msg)=>setPaddleError(msg || __("Your card was declined. Try another card or contact support."))
                    }) : /*#__PURE__*/ _jsx(Skeleton, {
                        active: true,
                        paragraph: {
                            rows: 6
                        }
                    }),
                    paddleError && /*#__PURE__*/ _jsx("div", {
                        style: {
                            marginTop: 12
                        },
                        children: /*#__PURE__*/ _jsx(InfoCard, {
                            variant: "soft-trust",
                            leftBorderColor: token.colorError,
                            children: paddleError
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            marginTop: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap"
                        },
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                style: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 10px",
                                    border: "1px solid #b7eb8f",
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: token.colorTextHeading
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(LockOutlined, {
                                        "aria-hidden": true,
                                        style: {
                                            color: token.colorSuccess,
                                            fontSize: 13
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: __("Secure, encrypted payment via Paddle")
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("img", {
                                src: paymentMethodsUrl,
                                alt: __("Accepted payment methods"),
                                style: {
                                    maxWidth: 240,
                                    height: "auto",
                                    opacity: 0.7
                                }
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
export { ChangePaymentDetailsForm };
